import { utils } from '@core';
import { createEntityAdapter, type EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import {
  type ActionPoint,
  type ChildRelationDevices,
  type DeviceAttribute,
  type DeviceData,
  type DeviceIncidentRule,
  type DeviceModel,
  SystemDeviceTypesIds,
} from '../models';
import { DevicesActions } from './devices.actions';

export interface DevicesState extends EntityState<DeviceData> {
  actionPoints: ActionPoint[];
  attributes: DeviceAttribute[];
  error?: Error;
  isDeviceUpdating: boolean;
  models: DeviceModel[];
}

export const devicesAdapter = createEntityAdapter<DeviceData>({});

export const initialDevicesState: DevicesState = devicesAdapter.getInitialState({
  actionPoints: [],
  attributes: [],
  isDeviceUpdating: false,
  models: [],
});

const normalizeDeviceData = (deviceData: DeviceData & { childRelations?: ChildRelationDevices[]; }) => {
  return {
    ...deviceData,
    outletDevices: deviceData.childRelations?.filter(device => device.childDevice.deviceTypeId === SystemDeviceTypesIds.OutletPDU),
    portDevices: deviceData.childRelations?.filter(device =>
      device.childDevice.deviceTypeId === SystemDeviceTypesIds.Port,
    ),
  };
};

const upsertIncidentRule = (device: DeviceData, rule: DeviceIncidentRule) => {
  const incidentRules = device.incidentRules ?? [];
  const hasIncidentRule = incidentRules.some(incidentRule => incidentRule.id === rule.id);

  return {
    ...device,
    incidentRules: hasIncidentRule
      ? incidentRules.map(incidentRule => incidentRule.id === rule.id ? rule : incidentRule)
      : [...incidentRules, rule],
  };
};

export const devicesReducer = createReducer(
  initialDevicesState,
  on(DevicesActions.loadAllClientDevices, state => {
    return {
      ...state,
      allDevicesLoaded: false,
    };
  }),
  on(DevicesActions.resetDeviceEntity, state => {
    return devicesAdapter.setAll([], {
      ...state,
      allDevicesLoaded: false,
    });
  }),
  on(DevicesActions.allClientDevicesLoaded, (state, action) => {
    return devicesAdapter.upsertMany(action.devices.map(normalizeDeviceData), {
      ...state,
      allDevicesLoaded: true,
    });
  }),

  on(DevicesActions.loadDevicesError, state => {
    return {
      ...state,
    };
  }),

  on(DevicesActions.loadDeviceDetails, (state) => ({ ...state, error: undefined })),
  on(DevicesActions.loadDeviceDetailsSuccess, (state, action) => {
    return devicesAdapter.setOne(normalizeDeviceData(action.deviceItem), { ...state });
  }),
  on(DevicesActions.loadDeviceDetailsFailure, (state, { error }) => ({ ...state, error })),

  on(DevicesActions.addNewDevice, state => ({ ...state, isDeviceCreating: true })),

  on(DevicesActions.addNewDeviceSuccess, (state, action) =>
    devicesAdapter.addOne(normalizeDeviceData(action.newDevice), { ...state, isDeviceCreating: false }),
  ),

  on(DevicesActions.addNewDeviceError, state => ({ ...state, isDeviceCreating: false })),

  on(DevicesActions.updateDeviceData, state => ({ ...state, isDeviceUpdating: true })),

  on(DevicesActions.deviceDataSuccessfullyUpdated, (state, action) => {
    return devicesAdapter.updateOne(
      {
        changes: normalizeDeviceData(action.device),
        id: action.device.id,
      },
      {
        ...state,
        isDeviceUpdating: false,
      },
    );
  }),

  on(DevicesActions.updateDeviceDataError, state => ({ ...state, isDeviceUpdating: false })),

  on(DevicesActions.loadDeviceCollectionBySpaceId, state => {
    return {
      ...state,
      spaceDeviceIsLoading: true,
    };
  }),

  on(DevicesActions.loadDeviceCollectionBySpaceIdSuccess, (state, actions) => {
    return devicesAdapter.upsertMany(actions.devices.map(normalizeDeviceData), { ...state, spaceDeviceIsLoading: false });
  }),
  on(DevicesActions.loadDeviceCollections, state => {
    return { ...state, collectionLoading: true };
  }),
  on(DevicesActions.loadDeviceCollectionsSuccess, (state, action) => {
    return {
      ...state,
      actionPoints: action.actionPoints,
      attributes: action.attributes,
      collectionLoading: false,
      models: action.models,
    };
  }),

  on(DevicesActions.createDeviceIncidentRuleSuccess, (state, action) => {
    const device = state.entities[action.rule.deviceId];

    if (!device) {
      return state;
    }

    return devicesAdapter.updateOne(
      {
        changes: upsertIncidentRule(device, action.rule),
        id: action.rule.deviceId,
      },
      state,
    );
  }),

  on(DevicesActions.updateDeviceIncidentRuleSuccess, (state, action) => {
    const device = state.entities[action.rule.deviceId];

    if (!device) {
      return state;
    }

    return devicesAdapter.updateOne(
      {
        changes: upsertIncidentRule(device, action.rule),
        id: action.rule.deviceId,
      },
      state,
    );
  }),

  on(DevicesActions.deleteDeviceIncidentRuleSuccess, (state, action) => {
    const device = state.entities[action.deviceId];

    if (!device) {
      return state;
    }

    return devicesAdapter.updateOne(
      {
        changes: {
          incidentRules: (device.incidentRules ?? []).filter(incidentRule => incidentRule.id !== action.incidentRuleId),
        },
        id: action.deviceId,
      },
      state,
    );
  }),

  on(DevicesActions.updateDevice, (state, action) => {
    const device = state.entities[action.device.id];

    if (!device) {
      return state;
    }

    const patch = utils.clearObject(normalizeDeviceData(action.device));
    const deviceModelInformation = patch['deviceModelInformation'] && device.deviceModelInformation
      ? { ...device.deviceModelInformation, ...patch['deviceModelInformation'] }
      : patch['deviceModelInformation'] ?? device.deviceModelInformation;

    return devicesAdapter.upsertOne({ ...device, ...patch, deviceModelInformation }, { ...state });
  }),

  on(DevicesActions.dropDevice, (state, action) => {
    return devicesAdapter.removeOne(action.deviceId, { ...state });
  }),
);
